import React from 'react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Camera, MessageSquare } from 'lucide-react';

interface MediaRequirementsEditorProps {
  supports_photos?: boolean;
  requires_photos?: boolean;
  supports_comments?: boolean;
  requires_comments?: boolean;
  min_photos?: number;
  max_photos?: number;
  onUpdate: (updates: Partial<MediaRequirements>) => void;
}

interface MediaRequirements {
  supports_photos: boolean;
  requires_photos: boolean;
  supports_comments: boolean;
  requires_comments: boolean;
  min_photos: number;
  max_photos: number;
}

export const MediaRequirementsEditor: React.FC<MediaRequirementsEditorProps> = ({
  supports_photos = true,
  requires_photos = false,
  supports_comments = true,
  requires_comments = false,
  min_photos = 0,
  max_photos = 5,
  onUpdate,
}) => {
  const evidenceLevel = 
    !supports_photos && !supports_comments ? 'visual' :
    supports_comments && !supports_photos ? 'comments' :
    supports_photos && supports_comments && requires_photos && requires_comments ? 'full' :
    supports_photos || supports_comments ? 'flexible' : 'visual';

  const handleEvidenceLevelChange = (level: string) => {
    switch (level) {
      case 'visual':
        onUpdate({
          supports_photos: false,
          requires_photos: false,
          supports_comments: false,
          requires_comments: false,
        });
        break;
      case 'comments':
        onUpdate({
          supports_photos: false,
          requires_photos: false,
          supports_comments: true,
          requires_comments: true,
        });
        break;
      case 'full':
        onUpdate({
          supports_photos: true,
          requires_photos: true,
          supports_comments: true,
          requires_comments: true,
          min_photos: 1,
        });
        break;
      case 'flexible':
        onUpdate({
          supports_photos: true,
          requires_photos: false,
          supports_comments: true,
          requires_comments: false,
        });
        break;
    }
  };

  return (
    <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
      <div className="space-y-2">
        <Label>Evidence Level</Label>
        <Select value={evidenceLevel} onValueChange={handleEvidenceLevelChange}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="visual">Visual Only (No documentation)</SelectItem>
            <SelectItem value="comments">Comments Required</SelectItem>
            <SelectItem value="flexible">Flexible (Optional photos & comments)</SelectItem>
            <SelectItem value="full">Full Documentation (Photos & comments required)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Camera className="h-4 w-4 text-muted-foreground" />
            <Label className="text-sm font-medium">Photo Settings</Label>
          </div>
          
          <div className="flex items-center justify-between">
            <Label htmlFor="supports-photos" className="text-sm font-normal">Enable Photos</Label>
            <Switch
              id="supports-photos"
              checked={supports_photos}
              onCheckedChange={(checked) => onUpdate({ 
                supports_photos: checked,
                requires_photos: checked ? requires_photos : false 
              })}
            />
          </div>

          {supports_photos && (
            <>
              <div className="flex items-center justify-between">
                <Label htmlFor="requires-photos" className="text-sm font-normal">Require Photos</Label>
                <Switch
                  id="requires-photos"
                  checked={requires_photos}
                  onCheckedChange={(checked) => onUpdate({ requires_photos: checked })}
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="min-photos" className="text-xs">Min Photos</Label>
                <Select
                  value={min_photos?.toString() || '0'}
                  onValueChange={(value) => onUpdate({ min_photos: parseInt(value) })}
                >
                  <SelectTrigger id="min-photos">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[0, 1, 2, 3, 4, 5].map((num) => (
                      <SelectItem key={num} value={num.toString()}>{num}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label htmlFor="max-photos" className="text-xs">Max Photos</Label>
                <Select
                  value={max_photos?.toString() || '5'}
                  onValueChange={(value) => onUpdate({ max_photos: parseInt(value) })}
                >
                  <SelectTrigger id="max-photos">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 10].map((num) => (
                      <SelectItem key={num} value={num.toString()}>{num}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
            <Label className="text-sm font-medium">Comment Settings</Label>
          </div>
          
          <div className="flex items-center justify-between">
            <Label htmlFor="supports-comments" className="text-sm font-normal">Enable Comments</Label>
            <Switch
              id="supports-comments"
              checked={supports_comments}
              onCheckedChange={(checked) => onUpdate({ 
                supports_comments: checked,
                requires_comments: checked ? requires_comments : false 
              })}
            />
          </div>

          {supports_comments && (
            <div className="flex items-center justify-between">
              <Label htmlFor="requires-comments" className="text-sm font-normal">Require Comments</Label>
              <Switch
                id="requires-comments"
                checked={requires_comments}
                onCheckedChange={(checked) => onUpdate({ requires_comments: checked })}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
